// React
import React from 'react'
import PropTypes from 'prop-types'

// Libraries
import { clsx } from 'clsx'

// Components
import BCartPicker from 'ui/blocks/Picker/types/cart'
import BPicker from 'ui/blocks/Picker'
import DateTimePicker from 'ui/components/Picker/DateTimePicker'
import Icon from 'ui/components/Icon'

// Hooks
import { usePicker } from 'ui/components/Picker/hooks'
import { useFormikContext } from 'formik'

// Shared
import { Field } from 'shared/form'
import { useTranslation } from 'shared/utils/useTranslation'

export const FreePicker = ({ children, ...props }) => {
  const picker = usePicker()

  const form = useFormikContext()
  const { t } = useTranslation('user')

  return (
    <BCartPicker.Period
      className={clsx({ 'no-times': !picker.useTimes })}
      disableStyling={props.isMobile}
      disabled={props.disabled}
    >
      <BPicker.Field>
        <BPicker.Label>{t('user.store.start')}</BPicker.Label>
        <Field
          {...props}
          component={DateTimePicker}
          name="starts_at"
          onChange={props.onStartChange}
          onBlur={props.onStartBlur}
          isTimeSelectable={(timestring) => picker.isTimeSelectable({ type: 'start', timestring })}
          blockedDays={(date) => !picker.isDaySelectable({ type: 'start', date })}
          inputDateRef={picker.refs.fromDateRef}
          inputTimeRef={picker.refs.fromTimeRef}
          mode="from"
          data-tid="Starts at picker"
          disabledDate={!!props.disable?.starts_at}
          disabledTime={!!props.disable?.starts_at}
          onMonthChange={props.onMonthChange?.bind(null, { type: 'start' })}
          onOpen={props.onOpen?.bind(null, { type: 'start', values: form.values })}
          withAvailability
          hideLabel
          initialError
        />
      </BPicker.Field>
      <BPicker.Decorator mobile>
        <Icon icon="arrow-right" />
      </BPicker.Decorator>
      <BPicker.Field>
        <BPicker.Label mobile>{t('user.store.stop')}</BPicker.Label>
        <Field
          {...props}
          component={DateTimePicker}
          name="stops_at"
          onChange={props.onStopChange}
          onTimeChange={props.onStopTimeChange}
          onBlur={props.onStopBlur}
          isTimeSelectable={(timestring) => picker.isTimeSelectable({ type: 'stop', timestring })}
          blockedDays={(date) => !picker.isDaySelectable({ type: 'stop', date })}
          inputDateRef={picker.refs.tillDateRef}
          inputTimeRef={picker.refs.tillTimeRef}
          mode="to"
          data-tid="Stops at picker"
          disabledDate={!!props.disable?.stops_at}
          disabledTime={!!props.disable?.stops_at}
          onMonthChange={props.onMonthChange?.bind(null, { type: 'stop', values: form.values })}
          onOpen={props.onOpen?.bind(null, { type: 'stop', values: form.values })}
          withAvailability
          hideLabel
          initialError
        />
      </BPicker.Field>
      {children}
    </BCartPicker.Period>
  )
}

FreePicker.propTypes = {
  children: PropTypes.node,
  isMobile: PropTypes.bool,
  onStartChange: PropTypes.func,
  onStopChange: PropTypes.func,
  onStartBlur: PropTypes.func,
  onStopBlur: PropTypes.func,
  onOpen: PropTypes.func,
  onStopTimeChange: PropTypes.func,
  onMonthChange: PropTypes.func,
  isDaySelectable: PropTypes.func,
  isTimeSelectable: PropTypes.func,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  disable: PropTypes.shape({
    starts_at: PropTypes.bool,
    stops_at: PropTypes.bool,
    start_location_id: PropTypes.bool,
    stop_location_id: PropTypes.bool
  }),
  disabled: PropTypes.bool,
  setDefaultTime: PropTypes.func,
  fromDateRef: PropTypes.any,
  tillDateRef: PropTypes.any
}
